import React, { useEffect, useRef, useState } from 'react';
import {
  isoDateToDisplay,
  parseFlexibleDateInput,
  parseFlexibleTimeInput,
} from '../../utils/dateUtils';

const inputBaseClass =
  'w-full px-4 py-2.5 pr-10 border rounded-lg focus:outline-none focus:ring-2 transition-all';
const inputOkClass = 'border-gray-300 focus:ring-teal-500 focus:border-teal-500';
const inputErrorClass = 'border-red-300 focus:ring-red-500';

const CalendarIcon = () => (
  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
    />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export const GuideDateField = ({
  id,
  name,
  label,
  value,
  onChange,
  error,
  required = false,
  min,
  onValidationError,
}) => {
  const [display, setDisplay] = useState(() => isoDateToDisplay(value));
  const pickerRef = useRef(null);

  useEffect(() => {
    setDisplay(isoDateToDisplay(value));
  }, [value]);

  const commitIso = (iso) => {
    onChange({ target: { name, value: iso || '' } });
    setDisplay(iso ? isoDateToDisplay(iso) : '');
  };

  const handleBlur = () => {
    const trimmed = display.trim();
    if (!trimmed) {
      commitIso('');
      return;
    }
    const parsed = parseFlexibleDateInput(trimmed);
    if (!parsed) {
      onValidationError?.(name, 'Fecha inválida. Use DD/MM/AAAA');
      setDisplay(isoDateToDisplay(value));
      return;
    }
    if (min && parsed < min) {
      onValidationError?.(name, 'La fecha no puede ser anterior a la fecha de emisión');
      setDisplay(isoDateToDisplay(value));
      return;
    }
    onValidationError?.(name, null);
    commitIso(parsed);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.click();
      }
    } catch {
      el.click();
    }
  };

  const inputClass = `${inputBaseClass} ${error ? inputErrorClass : inputOkClass}`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          id={id}
          name={name}
          value={display}
          onChange={(e) => {
            const raw = e.target.value;
            setDisplay(raw);
            const parsed = parseFlexibleDateInput(raw);
            if (parsed) {
              onChange({ target: { name, value: parsed } });
            }
          }}
          onBlur={handleBlur}
          placeholder="DD/MM/AAAA"
          autoComplete="off"
          className={inputClass}
        />
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden
          value={value || ''}
          min={min || undefined}
          onChange={(e) => {
            onValidationError?.(name, null);
            commitIso(e.target.value);
          }}
          className="sr-only"
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-teal-600"
          title="Abrir calendario"
          tabIndex={-1}
        >
          <CalendarIcon />
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const GuideTimeField = ({
  id,
  name,
  label,
  value,
  onChange,
  error,
  onValidationError,
}) => {
  const [display, setDisplay] = useState(() => value || '');
  const pickerRef = useRef(null);

  useEffect(() => {
    setDisplay(value || '');
  }, [value]);

  const commitTime = (time) => {
    onChange({ target: { name, value: time || '' } });
    setDisplay(time || '');
  };

  const handleBlur = () => {
    const trimmed = display.trim();
    if (!trimmed) {
      commitTime('');
      return;
    }
    const parsed = parseFlexibleTimeInput(trimmed);
    if (!parsed) {
      onValidationError?.(name, 'Hora inválida. Use HH:mm');
      setDisplay(value || '');
      return;
    }
    onValidationError?.(name, null);
    commitTime(parsed);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.click();
      }
    } catch {
      el.click();
    }
  };

  const inputClass = `${inputBaseClass} ${error ? inputErrorClass : inputOkClass}`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          id={id}
          name={name}
          value={display}
          onChange={(e) => {
            const raw = e.target.value;
            setDisplay(raw);
            const parsed = parseFlexibleTimeInput(raw);
            if (parsed) {
              onChange({ target: { name, value: parsed } });
            }
          }}
          onBlur={handleBlur}
          placeholder="HH:mm"
          autoComplete="off"
          className={inputClass}
        />
        <input
          ref={pickerRef}
          type="time"
          tabIndex={-1}
          aria-hidden
          value={value || ''}
          onChange={(e) => {
            onValidationError?.(name, null);
            commitTime(e.target.value ? e.target.value.slice(0, 5) : '');
          }}
          className="sr-only"
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-teal-600"
          title="Abrir selector de hora"
          tabIndex={-1}
        >
          <ClockIcon />
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
